import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FormEventHandler } from 'react';
import { useFormContext } from 'react-hook-form';

import { DatePicker } from '@/components/Form/DatePicker';
import { EmploymentBusinessCategorySelection } from '@/components/Form/Employment/EmploymentBusinessCategorySelection';
import { EmploymentOccupationSelection } from '@/components/Form/Employment/EmploymentOccupationSelection';
import { EmploymentRiskSectorSelection } from '@/components/Form/Employment/EmploymentRiskSectorSelection';
import EmploymentRiskSectorQuestion from '@/components/Form/Employment/EmploymentRiskyQuestion';
import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { ActionBar } from '@/components/Layout';

import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';
import { EMPLOYED_TYPE_OPTIONS } from '@/constants/employment';
import { PHONE_PATTERN } from '@/constants/patterns';
import { EMPLOYER_MAX_LENGTH } from '@/constants/places';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs/selectors';
import { selectTerritories } from '@/redux/countries/selectors';

import { OccupationType } from '@/types/form';

import { getDataTestId } from '@/utils/browser';
import { isCaymanUser, isGuyanaUser } from '@/utils/user';
import { isFutureDate } from '@/utils/validation';

import { EmploymentInfoForm } from './helper';

import { EmployerFormContainer } from './EmploymentInfo.styled';

type EmployerFormProps = {
  onSubmit: FormEventHandler<HTMLDivElement>;
  onClose: () => void;
};

export function EmployerForm({
  onSubmit,
  onClose,
}: Readonly<EmployerFormProps>) {
  const { t, country } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const { phoneCodes } = useAppSelector(selectTerritories);
  const { occupations } = useAppSelector(selectConfigsState);
  const { dateFormat } = useAppSelector(selectConfigsState);
  const {
    control,
    watch,
    setValue,
    formState: { errors: visibleErrors, isValid },
  } = useFormContext<EmploymentInfoForm>();

  const watchEmploymentDesc = watch('employmentDetailed.employmentDesc');

  const countryOptions = COUNTRIES_WITH_POPULAR.map(item => ({
    ...item,
    text: country[item.value],
  }));

  const isCayman = isCaymanUser(user);
  const isGuyana = isGuyanaUser(user);
  return (
    <EmployerFormContainer onSubmit={onSubmit}>
      <Typography variant="h3" mb={2}>
        {t('employerForm.title')}
      </Typography>
      {isCayman ? (
        <EmploymentBusinessCategorySelection<EmploymentInfoForm>
          control={control}
          name="employmentDetailed.businessCategoryCode"
          label={t('employerForm.form.businessCategory.label')}
        />
      ) : (
        <Select
          options={EMPLOYED_TYPE_OPTIONS.map(option => ({
            value: option.value,
            text: t(option.text),
          }))}
          control={control}
          name="employmentDetailed.employmentDesc"
          error={!!visibleErrors.employmentDetailed?.employmentDesc}
          helperText={visibleErrors.employmentDetailed?.employmentDesc?.message}
          rules={{
            required: t('employerForm.form.employedType.required'),
          }}
          fullWidth
          placeholder={t('employerForm.form.employedType.placeholder')}
          label={t('employerForm.form.employedType.label')}
          onSelectChange={event => {
            const newEmployedType = event.target.value;
            if (newEmployedType !== watchEmploymentDesc) {
              setValue('employmentDetailed.occupationCode', '');
            }
          }}
          useDrawerOnMobile
          withSearchBar
        />
      )}
      <TextInput
        name="employmentDetailed.employerName"
        control={control}
        error={!!visibleErrors.employmentDetailed?.employerName}
        helperText={visibleErrors.employmentDetailed?.employerName?.message}
        fullWidth
        label={t('employerForm.form.employerName.label')}
        rules={{
          required: t('employerForm.form.employerName.required'),
          maxLength: {
            value: EMPLOYER_MAX_LENGTH.employerName,
            message: t('employerForm.form.employerName.maxLength', {
              number: EMPLOYER_MAX_LENGTH.employerName,
            }),
          },
        }}
      />
      <TextInput
        name="employmentDetailed.employerAddress"
        control={control}
        error={!!visibleErrors.employmentDetailed?.employerAddress}
        helperText={visibleErrors.employmentDetailed?.employerAddress?.message}
        fullWidth
        label={t('employerForm.form.address.label')}
        rules={{
          required: t('employerForm.form.address.required'),
          maxLength: {
            value: EMPLOYER_MAX_LENGTH.employerAddress,
            message: t('employerForm.form.address.maxLength', {
              number: EMPLOYER_MAX_LENGTH.employerAddress,
            }),
          },
        }}
      />
      <Select
        options={countryOptions}
        control={control}
        name="employmentDetailed.employerCountry"
        error={!!visibleErrors.employmentDetailed?.employerCountry}
        helperText={visibleErrors.employmentDetailed?.employerCountry?.message}
        rules={{
          required: t('employerForm.form.employerCountry.required'),
        }}
        fullWidth
        placeholder={t('employerForm.form.employerCountry.placeholder')}
        label={t('employerForm.form.employerCountry.label')}
        useDrawerOnMobile
        withSearchBar
      />
      <Grid container columnSpacing={1.5} rowSpacing={2}>
        <Grid item xs={5}>
          <Select
            options={phoneCodes}
            control={control}
            name="employmentDetailed.employerPhoneCode"
            fullWidth
          />
        </Grid>
        <Grid item xs={7}>
          <TextInput
            type="tel"
            inputMode="tel"
            name="employmentDetailed.employerPhone"
            placeholder={t('employerForm.form.phone.placeholder')}
            control={control}
            error={!!visibleErrors.employmentDetailed?.employerPhone}
            helperText={
              visibleErrors.employmentDetailed?.employerPhone?.message
            }
            inputProps={{ maxLength: 7 }}
            rules={{
              required: t('employerForm.form.phone.required'),
              pattern: {
                value: new RegExp(PHONE_PATTERN),
                message: t('employerForm.form.phone.pattern'),
              },
            }}
          />
        </Grid>
      </Grid>

      <DatePicker
        name="employmentDetailed.startDate"
        label={t('employerForm.form.startDate.label')}
        format={dateFormat}
        control={control}
        error={!!visibleErrors.employmentDetailed?.startDate}
        helperText={visibleErrors.employmentDetailed?.startDate?.message}
        rules={{
          required: t('employerForm.form.startDate.required'),
          validate: {
            isNotFutureDate: value =>
              isFutureDate(value as string)
                ? t('employerForm.form.startDate.cannotBeFuture')
                : undefined,
          },
        }}
        disableFuture
      />
      {isCayman ? (
        <Select
          options={occupations}
          control={control}
          name="employmentDetailed.occupationCode"
          error={!!visibleErrors.employmentDetailed?.occupationCode}
          helperText={visibleErrors.employmentDetailed?.occupationCode?.message}
          rules={{
            required: t('employerForm.form.occupationCode.required'),
          }}
          fullWidth
          placeholder={t('employerForm.form.occupationCode.placeholder')}
          label={t('employerForm.form.occupationCode.label')}
          useDrawerOnMobile
          withSearchBar
        />
      ) : (
        <EmploymentOccupationSelection<EmploymentInfoForm>
          control={control}
          name="employmentDetailed.occupationCode"
          label={t('employerForm.form.occupationCode.label')}
          occupationType={watchEmploymentDesc as OccupationType}
        />
      )}
      {!isCayman && !isGuyana && (
        <EmploymentRiskSectorQuestion<EmploymentInfoForm>
          control={control}
          name="riskSectorFlag"
          options={[
            { value: true, label: t('common.yes') },
            { value: false, label: t('common.no') },
          ]}
        />
      )}
      {isGuyana && (
        <EmploymentRiskSectorSelection<EmploymentInfoForm>
          control={control}
          name="employmentDetailed.riskSectorCategory"
        />
      )}
      <ActionBar>
        <Button
          {...getDataTestId(`employer-form-cancel-button`)}
          variant="outlined"
          fullWidth
          onClick={onClose}
        >
          {t('employerForm.form.button.cancel')}
        </Button>
        <Button
          {...getDataTestId(`employer-form-confirm-button`)}
          fullWidth
          type="submit"
          disabled={!isValid}
        >
          {t('employerForm.form.button.confirm')}
        </Button>
      </ActionBar>
      <FormErrorLogger formName="EmployerForm" control={control} />
    </EmployerFormContainer>
  );
}
